export type SkillValue = number | string;
export type SuggestionKind = 'canonical' | 'custom';
export type ResolvedSuggestion = {
  key: string;
  text: string;
  kind: SuggestionKind;
  value: SkillValue;
};
type TaxonomyItem = { id: number; title: string };

export function normalizeCustomTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim();
}

export function normalizeTitle(title: string): string {
  return normalizeCustomTitle(title).toLocaleLowerCase();
}

export function isTitleSelected(
  selected: SkillValue[],
  items: TaxonomyItem[],
  normalizedTitle: string,
): boolean {
  return selected.some((value) => {
    if (typeof value === 'number') {
      const item = items.find((localItem) => localItem.id === value);
      return item ? normalizeTitle(item.title) === normalizedTitle : false;
    }

    return typeof value === 'string' && normalizeTitle(value) === normalizedTitle;
  });
}

// Text suggestions resolve exactly like typed input: an exact (normalized) taxonomy title
// becomes that item's id, anything else is a custom value, and only when custom input is allowed.
// Already-selected values and duplicates are dropped; numeric ids keep taxonomy item order.
export function resolveSuggestions(options: {
  suggestions: SkillValue[];
  items: TaxonomyItem[];
  selected: SkillValue[];
  allowCustomInput: boolean;
}): ResolvedSuggestion[] {
  const { suggestions, items, selected, allowCustomInput } = options;
  const result: ResolvedSuggestion[] = [];
  const seen = new Set<string>();
  const suggestedIds = suggestions.filter((s): s is number => typeof s === 'number');

  const pushCanonical = (item: TaxonomyItem) => {
    const key = normalizeTitle(item.title);
    if (seen.has(key) || selected.includes(item.id) || isTitleSelected(selected, items, key)) {
      return;
    }
    seen.add(key);
    result.push({ key, text: item.title, kind: 'canonical', value: item.id });
  };

  items.filter((item) => suggestedIds.includes(item.id)).forEach((item) => pushCanonical(item));

  for (const suggestion of suggestions) {
    if (typeof suggestion !== 'string') {
      continue;
    }
    const title = normalizeCustomTitle(suggestion);
    if (!title) {
      continue;
    }
    const key = normalizeTitle(title);
    const matchingItem = items.find((item) => normalizeTitle(item.title) === key);
    if (matchingItem) {
      pushCanonical(matchingItem);
      continue;
    }
    if (!allowCustomInput || seen.has(key) || isTitleSelected(selected, items, key)) {
      continue;
    }
    seen.add(key);
    result.push({ key, text: title, kind: 'custom', value: title });
  }

  return result;
}
